import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { CopyButton } from "../components/CopyButton";
import {
  ApiError,
  type AdminStudy,
  type AdminStudySummaryRow,
  studies,
} from "../lib/api";

/**
 * /studies — My Studies (ported from `_prototype/app/(admin)/studies/page.tsx`).
 * Matches the prototype visually: header with "My Studies" + dark Create-study
 * CTA, full-width card rows with name + StatusPill + ID + description, per-row
 * Duplicate icon + Edit button.
 *
 * Deferred polish: FilterDropdown chip for status (244 LoC of its own
 * component — task #15 follow-up), bulk select + Archive confirm dialog
 * (use per-row archive on the detail page meanwhile).
 */
export function Studies() {
  const navigate = useNavigate();
  const [list, setList] = useState<AdminStudy[] | null>(null);
  const [summary, setSummary] = useState<AdminStudySummaryRow[] | null>(null);
  const [creating, setCreating] = useState(false);
  const [duplicatingId, setDuplicatingId] = useState<string | null>(null);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Delete flow: the study pending confirmation, plus in-flight + error state
  // for the destructive DeleteStudyDialog.
  const [deleteTarget, setDeleteTarget] = useState<AdminStudy | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  // Transient success confirmation shown after a delete (auto-dismisses).
  const [toast, setToast] = useState<{ key: number; message: string } | null>(
    null,
  );

  useEffect(() => {
    studies
      .list()
      .then(({ studies: rows }) => setList(rows))
      .catch((err) => setError(formatApiError(err)));
    // Summary is non-fatal — if it 500s on first deploy (e.g. cost_usd
    // column wasn't backfilled), the page still renders the list without
    // counters. Better than failing the whole view over a summary.
    studies
      .summary()
      .then(({ rows }) => setSummary(rows))
      .catch(() => setSummary([]));
  }, []);

  // Auto-dismiss the success toast.
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 3200);
    return () => clearTimeout(t);
  }, [toast]);

  const summaryById = useMemo(() => {
    const m = new Map<string, AdminStudySummaryRow>();
    for (const r of summary ?? []) m.set(r.studyId, r);
    return m;
  }, [summary]);

  async function downloadTranscripts(s: AdminStudy) {
    if (downloadingId) return;
    setDownloadingId(s.id);
    try {
      const { filename, markdown } = await studies.transcripts(s.id);
      const blob = new Blob([markdown], { type: "text/markdown" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setDownloadingId(null);
    }
  }

  async function createStudy() {
    if (creating) return;
    setCreating(true);
    setError(null);
    try {
      const { study } = await studies.create();
      // Prepend so the new draft shows at the top if the user bounces
      // back without finishing the form.
      setList((prev) => (prev ? [study, ...prev] : [study]));
      navigate(`/studies/${study.id}`);
    } catch (err) {
      setError(formatApiError(err));
      setCreating(false);
    }
  }

  async function duplicate(id: string) {
    if (duplicatingId) return;
    setDuplicatingId(id);
    try {
      const { study } = await studies.duplicate(id);
      setList((prev) => (prev ? [study, ...prev] : prev));
      navigate(`/studies/${study.id}`);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setDuplicatingId(null);
    }
  }

  async function confirmDelete() {
    if (!deleteTarget || deleting) return;
    const target = deleteTarget;
    setDeleting(true);
    setDeleteError(null);
    try {
      const { deletedInterviews } = await studies.remove(target.id);
      setList((prev) => (prev ? prev.filter((x) => x.id !== target.id) : prev));
      setDeleteTarget(null);
      const name = target.name || "untitled study";
      setToast({
        key: Date.now(),
        message:
          deletedInterviews > 0
            ? `Deleted “${name}” and ${deletedInterviews} interview${
                deletedInterviews === 1 ? "" : "s"
              } (recordings + transcripts).`
            : `Deleted “${name}”.`,
      });
    } catch (err) {
      // Stay on the dialog so the message (e.g. 409 "pause or complete first")
      // is visible next to the action that triggered it.
      setDeleteError(formatApiError(err));
    } finally {
      setDeleting(false);
    }
  }

  return (
    <div className="min-h-full px-8 py-10 lg:px-14 lg:py-12">
      {toast && (
        <div
          key={toast.key}
          className="fixed top-6 left-1/2 -translate-x-1/2 z-50 inline-flex items-center gap-2 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-[13px] font-medium text-emerald-800 shadow-sm"
          role="status"
        >
          <svg width="14" height="14" viewBox="0 0 16 16" aria-hidden>
            <path
              d="M3 8.5l3.5 3.5L13 5"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          {toast.message}
        </div>
      )}
      {/* Header row */}
      <div className="flex items-start justify-between gap-6 mb-6">
        <h1 className="text-[34px] leading-[40px] font-semibold tracking-tight text-neutral-900">
          My Studies
        </h1>
        <button
          type="button"
          onClick={() => void createStudy()}
          disabled={creating}
          className="inline-flex items-center gap-1.5 rounded-md bg-neutral-900 hover:bg-neutral-800 active:bg-black disabled:bg-neutral-300 disabled:cursor-not-allowed px-3.5 py-2 text-[13px] font-medium text-white transition-colors shrink-0"
        >
          <svg width="14" height="14" viewBox="0 0 16 16" aria-hidden>
            <path
              d="M8 3v10M3 8h10"
              stroke="currentColor"
              strokeWidth="1.6"
              strokeLinecap="round"
            />
          </svg>
          <span>{creating ? "Creating…" : "Create study"}</span>
        </button>
      </div>

      {error && (
        <p className="text-[13px] text-red-700 mb-3" role="alert">
          {error}
        </p>
      )}

      {list === null && !error && (
        <p className="text-[13px] text-neutral-500">Loading…</p>
      )}

      {list !== null && list.length === 0 && (
        <div className="rounded-lg border border-neutral-200 bg-white px-8 py-10 text-center">
          <p className="text-[14px] text-neutral-500">
            No studies yet. Click{" "}
            <span className="font-semibold">Create study</span> to make one.
          </p>
        </div>
      )}

      {list && list.length > 0 && (
        <ul className="space-y-3">
          {list.map((s) => {
            const sum = summaryById.get(s.id);
            return (
              <li
                key={s.id}
                className="group relative rounded-lg border border-neutral-200 bg-white hover:bg-neutral-50 hover:border-neutral-400 transition-colors"
              >
                {/* Full-card link overlay — clicks navigate to the editor.
                    Action buttons sit above this via z-index + pointer-events. */}
                <Link
                  to={`/studies/${s.id}`}
                  aria-label={`Open ${s.name || "untitled study"}`}
                  className="absolute inset-0 rounded-lg focus:outline-none focus:ring-2 focus:ring-neutral-300"
                />
                <div className="relative pointer-events-none px-5 py-4">
                  <div className="flex items-start justify-between gap-6">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2.5 mb-1 flex-wrap">
                        <h2 className="text-[15px] font-semibold text-neutral-900 truncate">
                          {s.name || (
                            <span className="text-neutral-500 font-normal">
                              (untitled)
                            </span>
                          )}
                        </h2>
                        <StatusPill status={s.status} />
                        <span className="inline-flex items-center gap-1 font-mono text-[11px] text-neutral-400">
                          <span className="select-all">ID: {s.id}</span>
                          <span className="relative z-10 pointer-events-auto">
                            <CopyButton value={s.id} ariaLabel="Copy study ID" />
                          </span>
                        </span>
                      </div>
                      {s.description && (
                        <p className="text-[13px] leading-relaxed text-neutral-600">
                          {s.description}
                        </p>
                      )}
                      {!s.description && (
                        <p className="text-[12px] text-neutral-400">
                          <code className="font-mono">{s.slug}</code> · voice:{" "}
                          {s.voice}
                        </p>
                      )}

                      {/* ── Counters strip ──────────────────────────────────
                          Only renders when the /summary call has returned. A
                          missing row in the summary map = no interviews yet;
                          we hide the strip in that case to avoid showing
                          0/0/0/$0 on every brand-new draft. */}
                      {sum && sum.totalCount > 0 && (
                        <CountersStrip
                          summary={sum}
                          enrollmentCap={s.maxValidInterviews}
                        />
                      )}
                    </div>

                    <div className="relative z-10 pointer-events-auto flex items-center gap-2 shrink-0">
                      <button
                        type="button"
                        onClick={() => {
                          setDeleteError(null);
                          setDeleteTarget(s);
                        }}
                        disabled={s.status === "published"}
                        aria-label={`Delete ${s.name || "untitled study"}`}
                        title={
                          s.status === "published"
                            ? "Pause or complete this study before deleting"
                            : "Delete study (permanent)"
                        }
                        className="inline-flex items-center justify-center w-8 h-8 rounded-md border border-neutral-200 bg-white text-neutral-700 hover:bg-red-50 hover:border-red-300 hover:text-red-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-white disabled:hover:border-neutral-200 disabled:hover:text-neutral-700"
                      >
                        <svg width="12" height="12" viewBox="0 0 16 16" aria-hidden>
                          <path
                            d="M2.5 4h11M6 4V2.6a.6.6 0 0 1 .6-.6h2.8a.6.6 0 0 1 .6.6V4m2.1 0v9a1 1 0 0 1-1 1H4.4a1 1 0 0 1-1-1V4M6.5 7v4M9.5 7v4"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="1.3"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </button>
                      <button
                        type="button"
                        onClick={() => void downloadTranscripts(s)}
                        disabled={
                          downloadingId !== null ||
                          !sum ||
                          sum.validCount === 0
                        }
                        aria-label={`Download valid transcripts for ${s.name}`}
                        title={
                          !sum || sum.validCount === 0
                            ? "No valid interviews yet"
                            : "Download valid transcripts (Markdown)"
                        }
                        className="inline-flex items-center justify-center w-8 h-8 rounded-md border border-neutral-200 bg-white hover:bg-neutral-50 hover:border-neutral-300 text-neutral-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        {downloadingId === s.id ? (
                          <span className="text-[14px] leading-none">…</span>
                        ) : (
                          <svg
                            width="12"
                            height="12"
                            viewBox="0 0 16 16"
                            aria-hidden
                          >
                            <path
                              d="M8 2v8M4 7l4 4 4-4M2 13h12"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="1.6"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        )}
                      </button>
                      <button
                        type="button"
                        onClick={() => void duplicate(s.id)}
                        disabled={duplicatingId !== null}
                        aria-label={`Duplicate ${s.name}`}
                        title="Duplicate study"
                        className="inline-flex items-center justify-center w-8 h-8 rounded-md border border-neutral-200 bg-white hover:bg-neutral-50 hover:border-neutral-300 text-neutral-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {duplicatingId === s.id ? (
                          <span className="text-[14px] leading-none">…</span>
                        ) : (
                          <svg
                            width="12"
                            height="12"
                            viewBox="0 0 16 16"
                            aria-hidden
                          >
                            <rect
                              x="2"
                              y="4"
                              width="9"
                              height="10"
                              rx="1.2"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="1.4"
                            />
                            <path
                              d="M5 4V3a1 1 0 0 1 1-1h7a1 1 0 0 1 1 1v8a1 1 0 0 1-1 1h-1"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="1.4"
                              strokeLinejoin="round"
                            />
                          </svg>
                        )}
                      </button>
                      <Link
                        to={`/studies/${s.id}`}
                        className="inline-flex items-center gap-1.5 rounded-md bg-neutral-900 hover:bg-neutral-800 px-3 py-1.5 text-[12px] font-medium text-white transition-colors"
                      >
                        <svg
                          width="11"
                          height="11"
                          viewBox="0 0 16 16"
                          aria-hidden
                        >
                          <path
                            d="M11 2l3 3-9 9-4 1 1-4 9-9z"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                        Edit
                      </Link>
                    </div>
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {deleteTarget && (
        <DeleteStudyDialog
          study={deleteTarget}
          interviewCount={
            summary === null
              ? null
              : (summaryById.get(deleteTarget.id)?.totalCount ?? 0) +
                (summaryById.get(deleteTarget.id)?.archivedCount ?? 0)
          }
          deleting={deleting}
          error={deleteError}
          onCancel={() => {
            setDeleteTarget(null);
            setDeleteError(null);
          }}
          onConfirm={() => void confirmDelete()}
        />
      )}
    </div>
  );
}

// ── Counters strip — per-card stats row (§11.8). Renders inline below the
//    description so the eye can scan: counts → enrollment → cost. Compact
//    text-only design (no big tiles) to keep the list rows scannable. ─────

function CountersStrip({
  summary,
  enrollmentCap,
}: {
  summary: AdminStudySummaryRow;
  enrollmentCap: number | null;
}) {
  const enrollmentPct =
    enrollmentCap && enrollmentCap > 0
      ? Math.min(100, Math.round((summary.validCount / enrollmentCap) * 100))
      : null;

  return (
    <div className="mt-3 flex items-center gap-4 flex-wrap text-[11px] text-neutral-600">
      <Counter label="Total" value={summary.totalCount} />
      <span className="text-neutral-200">·</span>
      <Counter
        label="Valid"
        value={summary.validCount}
        valueClass="text-emerald-700"
      />
      <Counter
        label="Invalid"
        value={summary.invalidCount}
        valueClass="text-rose-700"
      />
      {summary.inProgressCount > 0 && (
        <Counter
          label="In&nbsp;progress"
          value={summary.inProgressCount}
          valueClass="text-amber-700"
        />
      )}
      <span className="text-neutral-200">·</span>

      {enrollmentCap && enrollmentCap > 0 ? (
        <span className="inline-flex items-center gap-1.5">
          <span className="text-[10px] uppercase tracking-wider text-neutral-500 font-medium">
            Enrollment
          </span>
          <span className="font-semibold text-neutral-900 tabular-nums">
            {summary.validCount}
            <span className="text-neutral-400">/{enrollmentCap}</span>
          </span>
          {/* Tiny inline progress bar — mirrors what the prototype shows on
              the My Studies card. */}
          <span
            className="inline-block h-1 w-12 rounded-full bg-neutral-100 overflow-hidden"
            aria-hidden
          >
            <span
              className={`block h-full ${
                enrollmentPct! >= 100 ? "bg-emerald-500" : "bg-neutral-700"
              }`}
              style={{ width: `${enrollmentPct}%` }}
            />
          </span>
        </span>
      ) : (
        <span className="inline-flex items-center gap-1">
          <span className="text-[10px] uppercase tracking-wider text-neutral-500 font-medium">
            Enrollment
          </span>
          <span className="text-neutral-400">uncapped</span>
        </span>
      )}

      <span className="text-neutral-200">·</span>

      <span className="inline-flex items-center gap-1">
        <span className="text-[10px] uppercase tracking-wider text-neutral-500 font-medium">
          Cost
        </span>
        <span className="font-semibold text-neutral-900 tabular-nums">
          ${summary.totalUsd.toFixed(2)}
        </span>
        <span
          className="text-[9px] uppercase tracking-wider text-neutral-400"
          title="Cost is computed from per-interview Realtime/insights usage × pricing; estimated, not authoritative."
        >
          est
        </span>
      </span>
    </div>
  );
}

function Counter({
  label,
  value,
  valueClass,
}: {
  label: string;
  value: number;
  valueClass?: string;
}) {
  return (
    <span className="inline-flex items-center gap-1">
      <span
        className="text-[10px] uppercase tracking-wider text-neutral-500 font-medium"
        dangerouslySetInnerHTML={{ __html: label }}
      />
      <span
        className={`font-semibold tabular-nums ${valueClass ?? "text-neutral-900"}`}
      >
        {value}
      </span>
    </span>
  );
}

// ── Status pill (ported from prototype) ────────────────────────────────────

const STATUS_STYLE: Record<
  string,
  { pill: string; dot: string; label: string }
> = {
  draft: {
    pill: "bg-neutral-100 text-neutral-600 border-neutral-200",
    dot: "bg-neutral-400",
    label: "Draft",
  },
  published: {
    pill: "bg-amber-50 text-amber-700 border-amber-200",
    dot: "bg-amber-400",
    label: "Running",
  },
  paused: {
    pill: "bg-blue-50 text-blue-700 border-blue-200",
    dot: "bg-blue-400",
    label: "Paused",
  },
  completed: {
    pill: "bg-emerald-50 text-emerald-700 border-emerald-200",
    dot: "bg-emerald-400",
    label: "Completed",
  },
  archived: {
    pill: "bg-neutral-100 text-neutral-600 border-neutral-200",
    dot: "bg-neutral-400",
    label: "Archived",
  },
};

export function StatusPill({ status }: { status: string }) {
  const style =
    STATUS_STYLE[status] ?? STATUS_STYLE.draft!;
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-md border px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${style.pill}`}
    >
      <span className={`w-1.5 h-1.5 rounded-full ${style.dot}`} />
      {style.label}
    </span>
  );
}

// ── ErrorBanner + helpers shared with other admin pages ─────────────────────

export function ErrorBanner({ message }: { message: string }) {
  return (
    <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
      <p className="text-body-s text-red-800 whitespace-pre-wrap">{message}</p>
    </div>
  );
}

export function formatApiError(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.status === 403) {
      return "Access denied. Your email isn't on the admin whitelist (check identity.cms_users).";
    }
    if (err.status === 401) {
      return "Not authenticated. Try signing out and back in.";
    }
    return `API error (${err.status}): ${err.message}`;
  }
  return err instanceof Error ? err.message : "Something went wrong";
}

// ── DeleteStudyDialog — destructive confirm for permanent study deletion ────
// Modeled on StudyDetail's ConfirmChangesDialog (Esc + backdrop close, body
// scroll lock). When the study has interviews — or the count hasn't loaded —
// the admin must type the study's exact name before Delete enables, a hard
// guard against irreversibly nuking real voice recordings + transcripts.

function DeleteStudyDialog({
  study,
  interviewCount,
  deleting,
  error,
  onCancel,
  onConfirm,
}: {
  study: AdminStudy;
  /** Interviews linked to the study; null = count not loaded (treat as "has data"). */
  interviewCount: number | null;
  deleting: boolean;
  error: string | null;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const [typed, setTyped] = useState("");

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !deleting) onCancel();
    };
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      document.removeEventListener("keydown", onKey);
    };
  }, [onCancel, deleting]);

  // Require type-to-confirm when the study has interviews, or the count
  // couldn't be loaded — never one-click a study that might hold data.
  const needsTypeConfirm = interviewCount === null || interviewCount > 0;
  const confirmPhrase = study.name.trim() || "DELETE";
  const canDelete =
    !deleting && (!needsTypeConfirm || typed.trim() === confirmPhrase);

  const countLabel =
    interviewCount === null
      ? "its interviews"
      : interviewCount === 1
        ? "1 interview"
        : `${interviewCount} interviews`;

  return (
    <div
      className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 p-4"
      onClick={() => !deleting && onCancel()}
      role="dialog"
      aria-modal="true"
      aria-labelledby="delete-study-title"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-lg shadow-2xl max-w-lg w-full p-6 relative"
      >
        <div className="text-[10px] uppercase tracking-wider text-red-600 font-semibold mb-2">
          Delete study
        </div>
        <h2
          id="delete-study-title"
          className="text-[18px] font-semibold text-neutral-900 mb-1.5"
        >
          Permanently delete “{study.name || "untitled study"}”?
        </h2>
        <p className="text-[13px] leading-relaxed text-neutral-600 mb-4">
          This deletes the study and {countLabel}, including{" "}
          <span className="font-medium text-neutral-800">
            all voice recordings and transcripts
          </span>{" "}
          and every other linked record. This{" "}
          <span className="font-semibold">cannot be undone</span>.
        </p>

        {needsTypeConfirm && (
          <label className="block mb-4">
            <span className="block text-[12px] text-neutral-600 mb-1.5">
              Type{" "}
              <span className="font-mono font-semibold text-neutral-900">
                {confirmPhrase}
              </span>{" "}
              to confirm
            </span>
            <input
              type="text"
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              disabled={deleting}
              autoFocus
              autoComplete="off"
              spellCheck={false}
              className="w-full rounded-md border border-neutral-300 bg-white px-3 py-2 text-[14px] text-neutral-900 focus:outline-none focus:ring-2 focus:ring-red-200 focus:border-red-400"
            />
          </label>
        )}

        {error && (
          <p className="text-[13px] text-red-700 mb-4" role="alert">
            {error}
          </p>
        )}

        <div className="flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={onCancel}
            disabled={deleting}
            className="text-[13px] font-medium text-neutral-700 hover:text-neutral-900 transition-colors px-3 py-2 disabled:opacity-40"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={!canDelete}
            className="inline-flex items-center rounded-md bg-red-600 hover:bg-red-700 disabled:bg-red-300 disabled:cursor-not-allowed px-4 py-2 text-[13px] font-semibold text-white shadow-sm transition-colors"
          >
            {deleting ? "Deleting…" : "Delete permanently"}
          </button>
        </div>
      </div>
    </div>
  );
}
